<template>
    <div>
        <page-header> Phone Pools </page-header>

        <team-phone-number-pools-controls
            v-model="fetchParams"
            @apply="applyFilters"
            @reset="resetFilters"
            :loading="loading"
        ></team-phone-number-pools-controls>

        <template v-if="!loading && !list.length">
            <empty-state v-if="!isEqual(defaultFilters(), fetchParams)" @reset="resetFilters" />
            <empty-state v-else>
                No phone pools yet.
                <vx-button
                    v-if="isImpersonatingOrPlatformAdmin"
                    data-test="add-new-from-box"
                    :href="`/settings/teams/${teamId}/phone-number-pools/new`"
                >
                    New Phone Pool
                    <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
            </empty-state>
        </template>

        <vx-table
            v-else-if="list.length"
            :class="isImpersonatingOrPlatformAdmin ? 'xl:grid-cols-7-auto' : 'xl:grid-cols-5-auto'"
            rowBreakpoint="xl"
        >
            <vx-table-row isHeader>
                <div class="xl:col-span-2">Name</div>
                <div>Phone Numbers</div>
                <div v-if="isImpersonatingOrPlatformAdmin">Vendor</div>
                <div v-if="isImpersonatingOrPlatformAdmin">Vendor ID</div>
                <div>Created at</div>
            </vx-table-row>

            <team-phone-number-pool-row
                v-for="(item, index) in list"
                v-model="list[index]"
                :key="list[index].id"
                @deleted="removeFromList($event)"
            ></team-phone-number-pool-row>
        </vx-table>

        <div v-if="list.length" class="mt-8 flex justify-between w-full gap-10">
            <vx-button
                v-if="fetchParams.cursor"
                @click="loadData()"
                color="muted-dark"
                size="xl"
                data-test="load-more-btn"
                class="grow-0"
                :loading="loading"
            >
                Load More
            </vx-button>
        </div>
    </div>
</template>

<script setup>
import PageHeader from '~/components/general/PageHeader.vue';
import EmptyState from '~/components/general/EmptyState.vue';
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { isEqual } from '~/utils/helpers';
import { VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import TeamPhoneNumberPoolsControls from '~/components/settings/teams/phone-number-pools/TeamPhoneNumberPoolsControls.vue';
import { onMounted } from 'vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { phoneNumberPoolsClient } from '~/services';
import TeamPhoneNumberPoolRow from '~/components/settings/teams/phone-number-pools/TeamPhoneNumberPoolRow.vue';

const { list, loading, fetchParams, fetchRecords } = useCursorPagination();
const teamId = Spark.state.currentTeam.id;
const isImpersonatingOrPlatformAdmin = Spark.isImpersonating || Spark.isPlatformAdmin;

const defaultFilters = () => ({
    per_page: 10,
    cursor: null,
    sort_field: 'id',
    sort_dir: 'desc',
});

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };

    loadData();
};

onMounted(() => {
    resetFilters();
});

const loadData = () => {
    fetchRecords(phoneNumberPoolsClient.getList(teamId, { ...fetchParams.value }));
};

const removeFromList = (itemId) => {
    list.value = list.value.filter((item) => item.id != itemId);

    if (!list.value.length) {
        loadData();
    }
};
</script>
